import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ETIQUETA_TIPO_TRABAJO, TIPOS_TRABAJO, type TipoLineaMaterial } from '@cmms/shared';
import { api } from '../servicios/api.js';
import { SelectorLista, useTiposActivo } from '../componentes/selectores.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { Aviso, ErroresFormulario } from '../componentes/piezas.js';
import { MaterialesCreados } from '../componentes/materiales-creados.js';
import { BotonExportar } from '../componentes/exportar-datos.js';

const GENERAL = 'GENERAL';

const ETIQUETA_TRABAJO: Record<string, string> = {
  GENERAL: 'General (todos los trabajos)',
  ...ETIQUETA_TIPO_TRABAJO,
};

interface ListaApi {
  tipoActivoId: string;
  tipoTrabajo: string | null;
  lineas: { id: string; nombre: string; tipo: TipoLineaMaterial; cantidad: number; nota: string | null; orden: number }[];
}

interface LineaEditable {
  clave: number;
  nombre: string;
  tipo: TipoLineaMaterial;
  cantidad: string;
  nota: string;
}

/**
 * Plantillas de materiales y herramientas por tipo de activo. Cada tipo tiene una
 * lista general y, si hace falta, una por tipo de trabajo. Las OT copian la que
 * les corresponde al abrirse; editar aquí no cambia las OT existentes.
 */
/** Huella de las líneas tal como se guardarían: sirve para saber si hubo cambios de verdad. */
function firma(lineas: LineaEditable[]): string {
  return JSON.stringify(
    lineas.map((l) => [l.nombre.trim(), l.tipo, Number(l.cantidad.replace(',', '.')), l.nota.trim()]),
  );
}

export function ListasMateriales(): JSX.Element {
  const cliente = useQueryClient();
  const { data: tipos } = useTiposActivo();
  const [tipoActivoId, setTipoActivoId] = useState('');
  const [trabajo, setTrabajo] = useState(GENERAL);
  const [lineas, setLineas] = useState<LineaEditable[]>([]);
  const [siguiente, setSiguiente] = useState(1);
  const [original, setOriginal] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: listas, isLoading } = useQuery({
    queryKey: ['listas-materiales', tipoActivoId],
    queryFn: () => api.get<ListaApi[]>(`/listas-materiales/tipos/${tipoActivoId}`),
    enabled: tipoActivoId !== '',
  });

  // Al cambiar el tipo de activo o el tipo de trabajo, se carga la lista correspondiente.
  useEffect(() => {
    if (!listas) {
      setLineas([]);
      setOriginal('');
      return;
    }
    const tipoTrabajo = trabajo === GENERAL ? null : trabajo;
    const lista = listas.find((l) => l.tipoTrabajo === tipoTrabajo);
    const cargadas = (lista?.lineas ?? []).map((l, i) => ({
      clave: i + 1,
      nombre: l.nombre,
      tipo: l.tipo,
      cantidad: String(l.cantidad),
      nota: l.nota ?? '',
    }));
    setLineas(cargadas);
    setOriginal(firma(cargadas));
    setSiguiente(cargadas.length + 1);
    setAviso(null);
  }, [listas, trabajo]);

  // Solo hay algo que guardar si la lista cambió frente a la última versión cargada o guardada.
  const hayCambios = firma(lineas) !== original;

  const guardar = useMutation({
    mutationFn: () =>
      api.put(`/listas-materiales/tipos/${tipoActivoId}`, {
        tipoTrabajo: trabajo === GENERAL ? null : trabajo,
        lineas: lineas.map((l) => ({
          nombre: l.nombre.trim(),
          tipo: l.tipo,
          cantidad: Number(l.cantidad.replace(',', '.')),
          nota: l.nota.trim() || null,
        })),
      }),
    onSuccess: async () => {
      setAviso(null);
      await cliente.invalidateQueries({ queryKey: ['listas-materiales', tipoActivoId] });
    },
  });

  const actualizar = (clave: number, cambio: Partial<LineaEditable>): void =>
    setLineas((previas) => previas.map((l) => (l.clave === clave ? { ...l, ...cambio } : l)));

  const guardarLista = (): void => {
    if (!hayCambios) return;
    const invalida = lineas.some((l) => l.nombre.trim() === '' || !(Number(l.cantidad.replace(',', '.')) > 0));
    if (invalida) {
      setAviso('Cada línea necesita un nombre y una cantidad mayor que cero.');
      return;
    }
    guardar.mutate();
  };

  return (
    <>
      <BotonVolver a="/dashboard" etiqueta="Volver al inicio" />
      <div className="cabecera-pagina">
        <div>
          <h1>Listas de materiales</h1>
          <p className="campo-nota">
            Lo que se lleva a cada tipo de trabajo. Las OT copian la lista al abrirse: cambiarla aquí no altera las OT ya abiertas.
          </p>
        </div>
        <BotonExportar ruta="/listas-materiales" filtros={{}} />
      </div>

      <MaterialesCreados />

      <div className="panel">
        <div className="panel-cuerpo">
          <div className="rejilla-campos">
            <SelectorLista
              id="tipo-activo-lista"
              etiqueta="Tipo de activo"
              valor={tipoActivoId}
              alCambiar={(v) => {
                setTipoActivoId(v);
                setTrabajo(GENERAL);
              }}
              opciones={(tipos ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
              requerido
            />
            <SelectorLista
              id="tipo-trabajo-lista"
              etiqueta="Tipo de trabajo"
              valor={trabajo}
              alCambiar={setTrabajo}
              opciones={[GENERAL, ...TIPOS_TRABAJO].map((t) => ({ id: t, nombre: ETIQUETA_TRABAJO[t] ?? t }))}
              deshabilitado={tipoActivoId === ''}
            />
          </div>
        </div>
      </div>

      {tipoActivoId === '' ? (
        <p className="campo-nota" style={{ marginTop: 16 }}>
          Elija un tipo de activo para ver o editar su lista.
        </p>
      ) : null}
      {isLoading ? <p className="campo-nota" style={{ marginTop: 16 }}>Cargando…</p> : null}

      {tipoActivoId !== '' && !isLoading ? (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>{trabajo === GENERAL ? 'Lista general' : ETIQUETA_TRABAJO[trabajo]}</h2>
            <div className="acciones-cabecera">
              <button type="button" className="boton" disabled={guardar.isPending || !hayCambios} onClick={guardarLista}>
                {guardar.isPending ? 'Guardando…' : 'Guardar lista'}
              </button>
            </div>
          </div>
          <div className="panel-cuerpo">
            {aviso ? <Aviso tono="atencion">{aviso}</Aviso> : null}
            {guardar.isError ? <ErroresFormulario error={guardar.error} /> : null}

            {lineas.length === 0 ? (
              <p className="campo-nota">Sin líneas. Agregue la primera.</p>
            ) : (
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Tipo</th>
                      <th>Cantidad</th>
                      <th>Nota</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {lineas.map((l) => (
                      <tr key={l.clave}>
                        <td>
                          <div className="campo" style={{ marginBottom: 0 }}>
                            <input
                              aria-label="Nombre"
                              value={l.nombre}
                              maxLength={200}
                              onChange={(e) => actualizar(l.clave, { nombre: e.target.value })}
                            />
                          </div>
                        </td>
                        <td>
                          <SelectorLista
                            id={`tipo-linea-${l.clave}`}
                            valor={l.tipo}
                            alCambiar={(v) => actualizar(l.clave, { tipo: v as TipoLineaMaterial })}
                            opciones={[
                              { id: 'HERRAMIENTA', nombre: 'Herramienta' },
                              { id: 'MATERIAL', nombre: 'Material' },
                            ]}
                            requerido
                          /></td>
                        <td>
                          <div className="campo" style={{ marginBottom: 0, width: 90 }}>
                            <input
                              aria-label="Cantidad"
                              inputMode="decimal"
                              value={l.cantidad}
                              onChange={(e) => actualizar(l.clave, { cantidad: e.target.value })}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="campo" style={{ marginBottom: 0 }}>
                            <input
                              aria-label="Nota"
                              value={l.nota}
                              maxLength={500}
                              onChange={(e) => actualizar(l.clave, { nota: e.target.value })}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="acciones-fila">
                            <button
                              type="button"
                              className="boton secundario pequeno"
                              onClick={() => setLineas((p) => p.filter((x) => x.clave !== l.clave))}
                            >
                              Quitar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="boton secundario"
                onClick={() => {
                  setLineas((p) => [...p, { clave: siguiente, nombre: '', tipo: 'HERRAMIENTA', cantidad: '1', nota: '' }]);
                  setSiguiente((n) => n + 1);
                }}
              >
                Agregar línea
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
